import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { ModelType } from "../global-types/customization-types";
import { BackupInfo } from "./backup-types";

export type BackupSliceState = {
    backupRestoreInProgress: boolean;
    backupRestoreError: string | null;
    restoreModelType: ModelType;
    backupList: BackupInfo[];
    backupListLoading: boolean;
    backupListError: string | null;
    listModelType: ModelType;
};

export const initialState: BackupSliceState = {
    backupRestoreInProgress: false,
    backupRestoreError: null,
    restoreModelType: ModelType.None,
    backupList: [],
    backupListLoading: false,
    backupListError: null,
    listModelType: ModelType.None,
};

export const getInitialState = (): BackupSliceState => ({ ...initialState });

const backupSlice = createSlice({
    name: 'backup',
    initialState,
    reducers: {
        backupRestoreRequested(_state, _action: PayloadAction<{ appClient: MVisionAppClient; backupFilename: string; modelType: ModelType } | undefined>) {
            // Signal-only reducer — state is set in the saga via restoreModelTypeSet
        },

        restoreModelTypeSet(state, action: PayloadAction<ModelType>) {
            state.restoreModelType = action.payload;
        },

        backupRestoreStarted(state) {
            state.backupRestoreInProgress = true;
            state.backupRestoreError = null;
        },

        backupRestoreFinished(state, action: PayloadAction<{ success: boolean; errorMessage?: string }>) {
            state.backupRestoreInProgress = false;
            state.backupRestoreError = action.payload.success ? null : (action.payload.errorMessage || 'Backup restore failed.');
        },

        backupListFetchRequested(_state, _action: PayloadAction<{ appClient: MVisionAppClient; modelType: ModelType }>) {
            // Signal-only reducer — state is set in the saga via listModelTypeSet
        },

        listModelTypeSet(state, action: PayloadAction<ModelType>) {
            state.listModelType = action.payload;
        },

        backupRestoreErrorCleared(state) {
            state.backupRestoreError = null;
        },

        backupListFetchStarted(state) {
            state.backupList = [];
            state.backupListLoading = true;
            state.backupListError = null;
        },

        backupListFetched(state, action: PayloadAction<{ backups: BackupInfo[]; modelType: ModelType; error?: string }>) {
            if (action.payload.modelType !== state.listModelType) {
                return; // Discard stale results from a previous model type request
            }
            state.backupList = action.payload.backups;
            state.backupListLoading = false;
            state.backupListError = action.payload.error || null;
        },
    },
});

export const {
    backupRestoreRequested,
    restoreModelTypeSet,
    backupRestoreStarted,
    backupRestoreFinished,
    backupListFetchRequested,
    listModelTypeSet,
    backupRestoreErrorCleared,
    backupListFetchStarted,
    backupListFetched,
} = backupSlice.actions;

export default backupSlice.reducer;
